import classNames from 'classnames';
import { default as dayjs } from 'dayjs';
import DOMPurify from 'dompurify';
import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { trackFBSearchEvent } from '@/components/tracking_third_party/fbSearch';
import { trackTTSearchEvent } from '@/components/tracking_third_party/tiktokSearch';
import { TmFavouriteLocalStorage, TmFavourite, TmFavouriteList } from '@/tscapi/model/favourite';
import http from '@/tscapi/service/httpservice';
import { Cookie } from '@/tscapi/utils/cookie';
import { FavouriteStorage } from '@/tscapi/utils/favourites-storage';

import AdCard, { AdCardProps } from '../AdCard/AdCard';
import FraudModal from '../FraudModal/FraudModal';
import ProductCard, { ProductCategoryType } from '../ProductCard/ProductCard';
import OnAirButtons from '../PrpFilterPanel/OnAirButtons';
import PrpFilterPanel, { ratingSvgMapping } from '../PrpFilterPanel/PrpFilterPanel';
import PrpNoSearchResults from '../PrpNoSearchResults/PrpNoSearchResults';

import { en, fr } from './i18n';
import { IProduct } from './IProduct';
import { IPrp, IPrpDimensionStateRefinement, IPrpFavourites, IPrpDimensionState, IPrpPaging } from './IPrp';
import prpMock from './Prp.mock';

export interface PrpProps {
    data: IPrp;
}

export type FocusArea = 'products' | 'filter' | 'sort';

const Prp = () => {
    const { i18n, t } = useTranslation('prp');
    i18n.addResourceBundle('en', 'prp', en, true, true);
    i18n.addResourceBundle('fr', 'prp', fr, true, true);

    const BREAKPOINT = 1023;
    const DEFAULT_PAGE_SIZE = 48;
    const DEFAULT_PAGE_SORT_KEY = 'FeaturedProducts';
    const DEFAULT_DIMENSION_ID = '0';
    const PRP_CARD_GRID_ITEM_CLASSNAME = '.prp-card-grid-item';

    const [data, setData] = useState<IPrp>({
        ...prpMock.data,
        Products: [],
        DimensionStates: [],
    });

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSortKey, setPageSortKey] = useState('');
    const [pageDimensionId, setPageDimensionId] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [rdParameter, setRdParameter] = useState('');
    const [viParameter, setViParameter] = useState('');
    const [currentPageSize, setCurrentPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
    const [width, setWidth] = useState(window.innerWidth);
    const [currentFocusedCardIndex, setCurrentFocusedCardIndex] = useState(0);
    const [favourites, setFavourites] = useState<TmFavouriteLocalStorage[] | null>(null);
    const [currentFocusArea, setCurrentFocusArea] = useState<FocusArea>('products');
    const [isFetchComplete, setIsFetchComplete] = useState(false);
    const [showFraudModal, setShowFraudModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [jsonLD, setJsonLD] = useState('');

    const [isAdCardAvailable, setIsAdCardAvailable] = useState(false);
    const [adCardProps, setAdCardProps] = useState<AdCardProps | null>(null);

    /*
    console.log('%c data.DimensionStates:', 'color: purple; font-weight: bold;');
    console.log({ dataDimensionStates: data.DimensionStates });
    */
    /*
  console.log('%c pageDimensionId:', 'color: magenta; font-weight: bold;');
  console.log({ pageDimensionId });
  */

    const getProductImageURL = (prod: IProduct) => {
        if (prod != null && prod.ImageUrl != null && prod.ImageUrl.length > 0) {
            return 'https://item.tscimg.ca/TSC/' + prod.ImageUrl + '?impolicy=XL';
        }
        return '';
    };

    const getEdpNo = (prod: IProduct) => {
        const defEdp = prod.DefaultEdp;
        for (const edp of prod.Edps) {
            if (edp.EdpNo == prod.DefaultEdp) {
                return edp.EdpNo;
            }
        }
        return '';
    };

    const getUpcNo = (prod: IProduct) => {
        const defEdp = prod.DefaultEdp;
        for (const edp of prod.Edps) {
            if (edp.EdpNo == prod.DefaultEdp) {
                return edp.UpcNo;
            }
        }
        return '';
    };

    const getAppliedPrice = (prod: IProduct) => {
        const defEdp = prod.DefaultEdp;
        for (const edp of prod.Edps) {
            if (edp.EdpNo == prod.DefaultEdp) {
                return edp.AppliedPrice || '0.00';
            }
        }
        return '';
    };

    const getSafeHTML = (value: {}) => {
        // If value convert to JSON and escape / to prevent script tag in JSON
        const json = value ? JSON.stringify(value, null) : ''; /*.replace(/\//g, '\\/')*/
        return DOMPurify.sanitize(json);
    };

    const updateSchema = (prodResult: IPrp) => {
        //debugger;
        const todayDate: Date = new Date(Date.now());
        function makeDate(date: Date) {
            return new Date(+date);
        }

        const todaysDateFormatted = dayjs(makeDate(todayDate)).format('YYYY-MM-DD');
        const oneWeekLaterDateFormatted = dayjs(makeDate(todayDate)).add(7, 'day').format('YYYY-MM-DD');

        //let dateToString = formatWithOptions('yyyy-MM-dd');
        if (prodResult.Products == null || prodResult.Products.length == 0) {
            setJsonLD('');
            return;
        }

        //Construct Schema's mainEntity object
        const schema = {
            '@context': 'https://schema.org',
            '@type': 'SearchResultsPage',
            mainEntity: [],
        };

        const objEntity = {
            '@type': 'ItemList',
            name:
                prodResult.DimensionStates != null && prodResult.DimensionStates.length > 0
                    ? prodResult.DimensionStates[0].ListTitle
                    : 'ItemList',
            itemListOrder:
                'https://schema.org/ItemListOrderAscending' /* Order is not available in TmProductResult   */,
            itemListElement: [],
        };

        let position = 0;
        const cookie = new Cookie();
        const language = cookie.Language.toLowerCase();
        const getUrl = window.location;
        const baseUrl = getUrl.protocol + '//' + getUrl.host;

        for (const product of prodResult.Products) {
            position = position + 1;

            const oProd: { [k: string]: any } = {
                '@type': 'ListItem',
                position: position,
                item: {
                    '@type': 'Product',
                    name: product.Name,
                    image: getProductImageURL(product),
                    description: product.Name /* description is not available in TmProductForPRP, so Name is used  */,
                    brand: product.Brand != null ? product.Brand : 'Brand not provided',
                    offers: {
                        '@type': 'Offer',
                        priceCurrency: 'CAD',
                        url: baseUrl + product.LinkWithoutDomain + '&edp=' + getEdpNo(product),
                        itemCondition: 'https://schema.org/NewCondition',
                        priceValidUntil: product.IsShowstopper ? todaysDateFormatted : oneWeekLaterDateFormatted,
                        price:
                            language == 'en_ca'
                                ? getAppliedPrice(product).replace(/[^0-9\.]+/g, '')
                                : getAppliedPrice(product)
                                      .replace(/[^0-9\,]+/g, '')
                                      .replace(',', '.'),
                        availability: 'https://schema.org/InStock',
                    },
                },
            };

            if (getUpcNo(product)) {
                oProd.item.gtin12 = getUpcNo(product);
            }
            if (getEdpNo(product)) {
                oProd.item.sku = getEdpNo(product);
            }

            if (product.ProductReviewCount > 0) {
                const oAggregateRating = {
                    '@type': 'AggregateRating',
                    ratingValue: product.ProductReviewRating,
                    bestRating: '5',
                    worstRating: '0',
                    ratingCount: product.ProductReviewCount,
                };
                oProd.item['aggregateRating'] = Object.assign({}, oAggregateRating);
            }

            objEntity.itemListElement.push(oProd);
        }

        schema.mainEntity.push(objEntity);
        const json = schema;
        setJsonLD(getSafeHTML(json));
    };

    const filterPanelHandler = () => {
        if (isFilterPanelOpen) {
            setIsFilterPanelOpen(false);
        } else {
            setIsFilterPanelOpen(true);
        }
    };

    const getUserFavourites = (edp: number) => {
        const favouriteStorage = new FavouriteStorage();
        favouriteStorage.Favourites;
        setFavourites(favouriteStorage.Favourites);
    };

    const buildApiUrl = useCallback(
        (
            dimensionId: string,
            page: number,
            sortKey: string,
            searchTermKey: string,
            pageSize: number,
            rdValue: string,
            viValue: string,
        ) => {
            const lang = new Cookie().Language.substring(0, 2) || 'en';
            const baseUrl = '/api/v3/' + lang;
            let dimensionParam = '';
            if (dimensionId === DEFAULT_DIMENSION_ID || viValue === DEFAULT_DIMENSION_ID) {
                dimensionParam = '';
            } else if (viValue === '') {
                dimensionParam = `dimensions=${dimensionId}`;
            } else {
                if (viValue !== dimensionId && dimensionId === '') {
                    dimensionParam = `visitorId=${viValue}`;
                } else if (viValue !== dimensionId && dimensionId !== '') {
                    dimensionParam = `visitorId=${viValue}&dimensions=${dimensionId}`;
                } else {
                    dimensionParam = `dimensions=${dimensionId}`;
                }
            }

            const pageParam = page === 1 ? '' : `page=${page}`;
            const pageSizeParam = pageSize === DEFAULT_PAGE_SIZE ? '' : `pageSize=${pageSize}`;
            const sortKeyParam = sortKey !== '' && sortKey !== DEFAULT_PAGE_SORT_KEY ? 'sortKey=' + sortKey : '';
            const searchTermParam = searchTermKey !== '' ? 'searchTerm=' + searchTermKey : '';
            const rdParam = rdValue !== '' ? 'rd=' + rdValue : '';
            const params = [dimensionParam, pageParam, pageSizeParam, sortKeyParam, searchTermParam, rdParam].filter(
                (it) => it !== '',
            );
            const concat = params.join('&');
            const endpoint = concat !== '' ? `?${concat}` : '';
            const prpUrl = baseUrl + `/products${endpoint}`;
            return prpUrl;
        },
        [],
    );

    const buildPageUrl = useCallback(
        (
            dimensionId: string,
            page: number,
            sortKey: string,
            searchTermKey: string,
            pageSize: number,
            rdValue: string,
            viValue: string,
        ) => {
            /*
    console.log('%c dimensionId:', 'color: brown; font-weight: bold;');
    console.log({ dimensionId });
    */
            const baseUrl = '/pages';
            let dimensionParam = '';

            if (dimensionId === DEFAULT_DIMENSION_ID || viValue === DEFAULT_DIMENSION_ID) {
                dimensionParam = '';
            } else if (viValue === '') {
                dimensionParam = `dimensions=${dimensionId}`;
            } else {
                dimensionParam = `nav=vi:${viValue}`;
                if (viValue !== dimensionId && dimensionId === '') {
                    dimensionParam = `nav=vi:${viValue}`;
                } else if (viValue !== dimensionId && dimensionId !== '') {
                    dimensionParam = `${dimensionParam}&dimensions=${dimensionId}`;
                } else {
                    dimensionParam = `dimensions=${dimensionId}`;
                }
            }

            const pageParam = page === 1 ? '' : `page=${page}`;
            const pageSizeParam = pageSize === DEFAULT_PAGE_SIZE ? '' : `pageSize=${pageSize}`;
            const sortKeyParam = sortKey !== '' && sortKey !== DEFAULT_PAGE_SORT_KEY ? 'sortKey=' + sortKey : '';
            const searchTermParam = searchTermKey !== '' ? 'searchTerm=' + searchTermKey : '';
            const rdParam = rdValue !== '' ? 'rd=' + rdValue : '';
            const params = [dimensionParam, pageParam, pageSizeParam, sortKeyParam, searchTermParam, rdParam].filter(
                (it) => it !== '',
            );
            const concat = params.join('&');
            const endpoint = concat !== '' ? `?${concat}` : '';
            const pageUrl = baseUrl + `/productresults${endpoint}`;
            return pageUrl;
        },
        [],
    );

    const setProductRefinementAnalytics = (searchTerm: string, paging: IPrpPaging) => {
        const { TotalRecords, TotalPages, CurrentPage } = paging;
        const searchObj = { searchTerm, totalRecords: TotalRecords, totalPages: TotalPages, currentPage: CurrentPage };
        window.analyticsData.event = window.analyticsData.event || {};
        window.analyticsData.event.type = 'prp_refinement';
        window.analyticsData.search = searchObj;
    };

    const setMissingProductAnalytics = (searchTerm: string) => {
        const missingProductObj = { itemNo: searchTerm };
        const searchObj = { searchTerm, totalRecords: 0, totalPages: 0, currentPage: 0 };
        window.analyticsData.missingProduct = missingProductObj;
        window.analyticsData.search = searchObj;
    };

    const scrollToTopOfPRP = () => {
        const toScrollPosition = document.getElementById('to-scroll-position');
        toScrollPosition.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const navigateToPage = useCallback(
        async (
            dimensionId: string,
            page: number,
            sortKey: string,
            searchTermKey: string,
            pageSize: number,
            rdValue: string,
            viValue: string,
        ): Promise<string> => {
            const cachebleHeaders = {
                'Content-Type': 'application/json',
                currentdatetime: new Cookie().CurrentDate || new Date().toISOString(),
            };

            const prpUrl = buildApiUrl(dimensionId, page, sortKey, searchTermKey, pageSize, rdValue, viValue);

            try {
                const result = await http.get<IPrp>(prpUrl, {
                    headers: cachebleHeaders,
                    transformRequest: (data, headers) => {
                        delete headers.Authorization;
                        return data;
                    },
                });
                setData(result.data);
                setCurrentPage(result.data.Paging.CurrentPage);
                setPageSortKey(result.data.Sorting.SelectedSortOption.Key);
                updateSchema(result.data);
                generateAnalyticsFilters(result.data.DimensionStates);
                if (result.data.Products.length > 0)
                    setProductRefinementAnalytics(result.data.SearchTerm, result.data.Paging);
                if (result.data.Products === null || result.data.Products.length === 0)
                    setMissingProductAnalytics(result.data.SearchTerm);
                setIsFetchComplete(true);
            } catch (e) {
                setIsFetchComplete(true);
            }

            return buildPageUrl(dimensionId, page, sortKey, searchTermKey, pageSize, rdValue, viValue);
        },
        [setData],
    );

    const fetchAndSetProducts = useCallback(
        async (
            dimensionId: string,
            page: number,
            sortKey: string,
            searchTermKey: string,
            pageSize: number,
            rdValue: string,
            viValue: string,
        ) => {
            await navigateToPage(dimensionId, page, sortKey, searchTermKey, pageSize, rdValue, viValue);
        },
        [navigateToPage],
    );

    useEffect(() => {
        try {
            const searchString = window.sessionStorage.getItem('TSC_SearchString');
            if (searchString) {
                trackFBSearchEvent(searchString);
                trackTTSearchEvent(searchString);
                window.sessionStorage.removeItem('TSC_SearchString');
            }
        } catch (error) {
            console.error('Error accessing sessionStorage:', error);
        }
    }, []);

    useEffect(() => {
        const domAdCard = document.querySelector<HTMLAnchorElement>('.ad-card');

        const adCardData: AdCardProps = {
            ctaText: '',
            href: '',
            image: '',
            text: '',
            title: '',
            imageAlt: '',
            textBackgroundColour: '',
            textBackgroundOpacity: 0.5,
            textColour: '',
        };
        const queryString = window.location.search.slice(1);
        const queries: Record<string, string> = {};
        queryString.split('&').forEach((query) => {
            const [key, value] = query.split('=');
            queries[key] = value;
        });

        const pageSizeFromQuery = queries['pageSize'] || `$${DEFAULT_PAGE_SIZE}`;
        const parsedPageSize = parseInt(pageSizeFromQuery, 10) || DEFAULT_PAGE_SIZE;
        let pageSize = parsedPageSize;
        setCurrentPageSize(pageSize);

        if (!domAdCard) {
            setIsAdCardAvailable(false);
        } else {
            setIsAdCardAvailable(true);
            const domAdCardImage = document.querySelector<HTMLImageElement>('.ad-card__self');
            const domAdCardTitle = document.querySelector<HTMLSpanElement>('.ad-card__content-title');
            const domAdCardText = document.querySelector<HTMLSpanElement>('.ad-card__content-text');
            const domAdCardCta = document.querySelector<HTMLSpanElement>('.ad-card__content-cta__title');
            const domAdCardImageAltText = document.querySelector<HTMLSpanElement>('.ad-card__image-alt-text');
            const domAdCardTextColour = document.querySelector<HTMLSpanElement>('.ad-card__text-colour');
            const domAdCardTextBackgroundColour = document.querySelector<HTMLSpanElement>(
                '.ad-card__text-background-colour',
            );
            const domAdCardTextBackgroundOpacity = document.querySelector<HTMLSpanElement>(
                '.ad-card__text-background-opacity',
            );

            adCardData.href = domAdCard.getAttribute('href') || '';
            adCardData.image = domAdCardImage?.getAttribute('src') || '';
            adCardData.title = domAdCardTitle.textContent || '';
            adCardData.text = domAdCardText.textContent || '';
            adCardData.ctaText = domAdCardCta.textContent || '';
            adCardData.imageAlt = domAdCardImageAltText.textContent || '';
            adCardData.textColour = domAdCardTextColour.textContent || '';
            adCardData.textBackgroundColour = domAdCardTextBackgroundColour.textContent || '';
            adCardData.textBackgroundOpacity = parseFloat(domAdCardTextBackgroundOpacity.textContent) || 0.5;

            setAdCardProps(adCardData);
            domAdCard.remove();

            pageSize -= 1;
            setCurrentPageSize(pageSize);
        }

        let navQuery = queries['nav'];
        let dimensionId = '';
        let viValue = '';

        if (navQuery) {
            navQuery = decodeURIComponent(navQuery);
            const [head, tail] = navQuery.split(':');
            if (head === 'N' || head === 'n') {
                dimensionId = tail;
            } else if (head === 'vi') {
                //dimensionId = tail;
                setViParameter(tail);
                viValue = tail;
                if (queries['dimensions'] && queries['dimensions'] !== tail) {
                    dimensionId = queries['dimensions'];
                }
            } else {
                dimensionId = DEFAULT_DIMENSION_ID;
            }
        } else if (queries['dimensions']) {
            const dimensionsQuery = queries['dimensions'];
            dimensionId = dimensionsQuery;
        } else {
            dimensionId = DEFAULT_DIMENSION_ID;
        }

        setPageDimensionId(dimensionId);
        const pageQuery = queries['page'];
        let page = 1;

        if (pageQuery) {
            page = parseInt(pageQuery, 10) || 1;
            setCurrentPage(page);
        }

        const sortKey = queries['sortKey'] || '';
        setPageSortKey(sortKey);

        const searchTermKey = queries['searchterm'] || queries['searchTerm'] || '';
        setSearchTerm(searchTermKey);

        const rdTemp = queries['rd'] || '';
        const rd = rdTemp === 'false' ? '0' : rdTemp;
        setRdParameter(rd);

        fetchAndSetProducts(dimensionId, page, sortKey, searchTermKey, pageSize, rd, viValue).then(() => {
            const uat = new Cookie().UserAccessToken;
            if (!uat) {
                return;
            }

            const customerEdp = uat.Edp;
            if (!customerEdp) {
                return;
            }

            getUserFavourites(customerEdp);
        });
    }, [setPageDimensionId, fetchAndSetProducts]);

    const isSignedIn = useMemo(() => {
        const cookie = new Cookie();
        return !!cookie.UserAccessToken;
    }, [Cookie]);

    const isFavourite = (itemNo: string) => {
        if (!isSignedIn) {
            return false;
        }

        return favourites?.some((item) => item.ItemNumber === itemNo);
    };

    const products = useMemo(() => {
        return data.Products.map((el, i) => {
            const isLiked = isFavourite(el.ItemNo) || false;
            return (
                <div
                    className="prp__relative prp-card-grid-item prp-card-grid-item--product-card"
                    key={el.LinkWithoutDomain}
                >
                    <ProductCard
                        header={{ isLiked }}
                        productObj={el}
                        productHref={el.LinkWithoutDomain}
                        productImage={el.ImageUrl}
                        name={el.Name}
                        AvailableSwatches={el.AvailableSwatches}
                        brandName={el.Brand}
                        isPriceRange={el.IsPriceRange}
                        wasPriceRange={el.WasPriceRange}
                        reviewCount={el.ProductReviewCount}
                        rating={el.ProductReviewRating}
                        sizes={el.Sizes}
                        styles={el.Styles}
                        edps={el.Edps}
                        categoryPath={el.CategoryPath as ProductCategoryType}
                        enabledAddToCart={el.EnabledAddToCart}
                        isShowstopper={el.IsShowstopper}
                        showBadgeImage={el.ShowBadgeImage}
                        badgeImagePath={el.BadgeImagePath}
                        itemNo={el.ItemNo}
                        priceIsLabel={el.PriceIsLabel}
                        edp={el.DefaultEdp}
                        position={i}
                        currentPage={currentPage}
                        pageSize={currentPageSize}
                        queryId={data?.QueryId || null}
                        sellerType={el.SellerType}
                        showFraudModal={() => {
                            document.querySelector<HTMLElement>('.prp-wrapper')?.scrollIntoView();
                            setShowFraudModal(true);
                            $('#fraudModal').modal('show');
                        }}
                    />
                </div>
            );
        });
    }, [data, favourites]);

    const cards = useMemo(() => {
        const MOBILE_BREAKPOINT = 768;
        const isMobile = window.innerWidth < MOBILE_BREAKPOINT;
        const tmp: JSX.Element[] = [];

        if (isAdCardAvailable && adCardProps !== null) {
            const adCardComponent = (
                <AdCard
                    {...adCardProps}
                    key={adCardProps.image}
                    className="prp-card-grid-item prp-card-grid-item--ad-card"
                />
            );

            if (isMobile) {
                tmp.push(adCardComponent);
                tmp.push(products[0]);
                tmp.push(products[1]);
            } else {
                tmp.push(products[0]);
                tmp.push(products[1]);
                tmp.push(adCardComponent);
            }

            products.slice(2).forEach((p) => {
                tmp.push(p);
            });
        } else {
            products.forEach((p) => {
                tmp.push(p);
            });
        }

        return tmp.slice(0, currentPageSize);
    }, [products, currentPageSize, adCardProps, window.innerWidth]);

    // const scrollToTop = useCallback(() => {
    //   // window.scrollTo({
    //   //   top: 0,
    //   //   left: 0,
    //   //   behavior: 'smooth',
    //   // });

    //   document.querySelector<HTMLElement>('.prp')?.scrollIntoView();
    // }, []);

    const productGridKeyListener = (e: React.KeyboardEvent<HTMLDivElement>) => {
        const UP_ARROW = 'ArrowUp';
        const RIGHT_ARROW = 'ArrowRight';
        const DOWN_ARROW = 'ArrowDown';
        const LEFT_ARROW = 'ArrowLeft';

        let newIndex = -1;
        if (e.key === UP_ARROW) {
            newIndex = currentFocusedCardIndex - 3;
        } else if (e.key === RIGHT_ARROW) {
            newIndex = currentFocusedCardIndex + 1;
        } else if (e.key === DOWN_ARROW) {
            newIndex = currentFocusedCardIndex + 3;
        } else if (e.key === LEFT_ARROW) {
            newIndex = currentFocusedCardIndex - 1;
        }

        if (newIndex < 0) {
            newIndex = 0;
        } else if (newIndex >= cards.length) {
            newIndex = cards.length - 1;
        }

        setCurrentFocusedCardIndex(newIndex);
    };

    const prpKeyDownListener = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (!e.ctrlKey) {
            return;
        }

        if (currentFocusArea === 'products') {
            setCurrentFocusArea('filter');
            if (document.activeElement instanceof HTMLElement) {
                document.activeElement.blur();
            }
            const el = document.querySelector<HTMLElement>('.prp-filter-panel__blocks');
            el?.focus();
            return;
        }

        if (currentFocusArea === 'filter') {
            setCurrentFocusArea('sort');
            document.querySelector<HTMLElement>('.prp__count-and-shorting__shorting')?.focus();
            return;
        }

        if (currentFocusArea === 'sort') {
            setCurrentFocusArea('products');
            document.querySelector<HTMLElement>(PRP_CARD_GRID_ITEM_CLASSNAME)?.focus();
            return;
        }
    };

    const updateFilterAnalyticsData = (selectedFilters: any[]) => {
        const analyticsData = window.analyticsData || {};
        analyticsData.filters = selectedFilters;
        window.analytics_track = window.analytics_track || [];
        window.analytics_track.push({
            event: {
                type: 'prp_refinement',
            },
        });
    };

    const generateAnalyticsFilters = (dimensionStates: IPrpDimensionState[]) => {
        if (dimensionStates != null && dimensionStates.length > 0) {
            // let totalSelectedRefinements = 0;

            const analyticsFilters = [];

            for (const dimState of dimensionStates) {
                const groupName: string = dimState.ListTitle;

                dimState.ListTitle = dimState.ListTitle.substr(
                    0,
                    dimState.ListTitle.indexOf('(') > 0 ? dimState.ListTitle.indexOf('(') : dimState.ListTitle.length,
                ).trim();
                const selectedRefinements: number = dimState.Refinements.filter((dim) => dim.Selected).length;
                if (selectedRefinements > 0) {
                    dimState.ListTitle += ' (' + selectedRefinements + ')';
                    // totalSelectedRefinements += selectedRefinements;

                    const options = [];
                    const selectedFilters = dimState.Refinements.filter((dim) => dim.Selected);
                    for (const selectedFilter of selectedFilters) {
                        options.push({ name: selectedFilter.DimensionName });
                    }
                    analyticsFilters.push({ group: groupName, options: options });
                }
            }
            /*
      console.log('%c analyticsFilters:', 'color: purple; font-weight: bold;');
      console.log({ analyticsFilters });
      */
            updateFilterAnalyticsData(analyticsFilters);
        }
    };

    useEffect(() => {
        // window.addEventListener('resize', () => setWidth(window.innerWidth));
        if (width <= BREAKPOINT) {
            setIsFilterPanelOpen(false);
        } else {
            setIsFilterPanelOpen(true);
        }
    }, [width, BREAKPOINT, setIsFilterPanelOpen, setWidth]);

    useEffect(() => {
        const interval = setInterval(() => {
            setWidth(window.innerWidth);
        }, 300);

        return () => {
            clearInterval(interval);
        };
    }, [setWidth]);

    useEffect(() => {
        const allGridItems = document.querySelectorAll<HTMLElement>(PRP_CARD_GRID_ITEM_CLASSNAME);
        const currentItem = allGridItems[currentFocusedCardIndex];
        const isProductCard = currentItem?.classList.contains('prp-card-grid-item--product-card');

        if (isProductCard) {
            const index = currentFocusedCardIndex >= 2 ? currentFocusedCardIndex - 1 : currentFocusedCardIndex;
            document.querySelectorAll<HTMLElement>('.product-card')[index]?.focus();
        } else {
            document.querySelectorAll<HTMLAnchorElement>('.ad-card')[0]?.focus();
        }
    }, [currentFocusedCardIndex]);

    const onBackButtonEvent = (event: PopStateEvent) => {
        event.preventDefault();
        window.location.href = window.location.href;
    };

    useEffect(() => {
        window.addEventListener('popstate', onBackButtonEvent);
        return () => window.removeEventListener('popstate', onBackButtonEvent);
    }, []);

    const paginationIndices = useMemo(() => {
        const indices = [];
        const halfSize = 5;
        for (let i = 0; i < halfSize; i++) {
            indices.push(currentPage - i);
        }

        for (let i = 1; i < 2 * halfSize; i++) {
            indices.push(currentPage + i);
        }

        const maxPage = data.Paging.TotalPages <= 10 ? data.Paging.TotalPages : 10;
        const filtered = indices.filter((i) => i > 0 && i <= data.Paging.TotalPages).sort((a, b) => a - b);
        const arr: number[] = [];

        for (let i = 0; i < Math.min(maxPage, filtered.length); i++) {
            arr.push(filtered[i]);
        }

        return arr;
    }, [currentPage, data.Paging.TotalPages]);

    const appliedFilters = useMemo(() => {
        const filters: Array<IPrpDimensionStateRefinement> = [];
        if (!data.DimensionStates) {
            return [];
        }
        data.DimensionStates.forEach((state) => {
            state.Refinements.forEach((refinement) => {
                /*
        console.log('%c refinement:', 'color: lime; font-weight: bold;');
        console.log({ refinement });
        */
                if (refinement.Selected) {
                    filters.push(refinement);
                }
            });
        });
        return filters;
    }, [data, data.DimensionStates]);

    const onFilterChange = (refinement: IPrpDimensionStateRefinement) => {
        /*
    console.log('%c refinement:', 'color: red; font-weight: bold;');
    console.log({ refinement });
    */
        let newDimensionId = '';
        // const delimiter = window.location.href.includes('-') ? '-' : '%7C';
        const delimiter = '%7C';
        /*
    console.log('%c newDimensionId:', 'color: lime; font-weight: bold;');
    console.log({ delimiter });
    */

        // If not selected, then add a new selection
        // Else, remove the selection
        if (!refinement.Selected) {
            if (pageDimensionId === '') {
                newDimensionId = `${refinement.DimensionId}`;
            } else {
                newDimensionId = `${pageDimensionId}${delimiter}${refinement.DimensionId}`;
            }
        } else {
            if (viParameter === '') {
                newDimensionId = pageDimensionId.replace(`${delimiter}${refinement.DimensionId}`, '');
            } else {
                const splitted = pageDimensionId.split(delimiter);
                if (splitted.length === 1) {
                    newDimensionId = pageDimensionId.replace(`${refinement.DimensionId}`, '');
                } else {
                    if (splitted[0] === refinement.DimensionId) {
                        newDimensionId = pageDimensionId.replace(`${refinement.DimensionId}${delimiter}`, '');
                    } else {
                        newDimensionId = pageDimensionId.replace(`${delimiter}${refinement.DimensionId}`, '');
                    }
                }
            }
        }

        /*
    console.log('%c newDimensionId:', 'color: red; font-weight: bold;');
    console.log({ newDimensionId });
    */
        setPageDimensionId(newDimensionId);
        const newPageUrl = buildPageUrl(
            newDimensionId,
            1,
            data.Sorting.SelectedSortOption.Key,
            searchTerm,
            currentPageSize,
            rdParameter,
            viParameter,
        );
        /*
    console.log('%c newPageUrl:', 'color: gold; font-weight: bold;');
    console.log({ newPageUrl });
    */
        setIsLoading(true);
        fetchAndSetProducts(
            newDimensionId,
            1,
            data.Sorting.SelectedSortOption.Key,
            searchTerm,
            currentPageSize,
            rdParameter,
            viParameter,
        ).then(() => {
            // history.push(newPageUrl);
            // window.location.replace(newPageUrl);
            window.history.pushState({}, null, newPageUrl);
            setIsLoading(false);
        });
    };

    const removeAppliedFilter = (dimensionId: string) => {
        // const delimiter = window.location.href.includes('-') ? '-' : '%7C';
        const delimiter = '%7C';
        let newDimensionId = '';

        if (viParameter === '') {
            newDimensionId = pageDimensionId.replace(`${delimiter}${dimensionId}`, '');
        } else {
            const splitted = pageDimensionId.split(delimiter);
            if (splitted.length === 1) {
                newDimensionId = pageDimensionId.replace(`${dimensionId}`, '');
            } else {
                if (splitted[0] === dimensionId) {
                    newDimensionId = pageDimensionId.replace(`${dimensionId}${delimiter}`, '');
                } else {
                    newDimensionId = pageDimensionId.replace(`${delimiter}${dimensionId}`, '');
                }
            }
        }

        setPageDimensionId(newDimensionId);
        const newPageUrl = buildPageUrl(
            newDimensionId,
            1,
            data.Sorting.SelectedSortOption.Key,
            searchTerm,
            currentPageSize,
            rdParameter,
            viParameter,
        );
        setIsLoading(true);
        fetchAndSetProducts(
            newDimensionId,
            1,
            data.Sorting.SelectedSortOption.Key,
            searchTerm,
            currentPageSize,
            rdParameter,
            viParameter,
        ).then(() => {
            // history.push(newPageUrl);
            // window.location.replace(newPageUrl);
            window.history.pushState({}, null, newPageUrl);
            setIsLoading(false);
        });
    };

    const removeAllAppliedFilters = () => {
        const delimiter = pageDimensionId.includes('-') ? '-' : '%7C';
        let newDimensionId = '';

        if (viParameter === '') {
            newDimensionId = pageDimensionId.split(delimiter)[0];
        } else {
            newDimensionId = '';
        }

        setPageDimensionId(newDimensionId);
        const newPageUrl = buildPageUrl(
            newDimensionId,
            1,
            data.Sorting.SelectedSortOption.Key,
            searchTerm,
            currentPageSize,
            rdParameter,
            viParameter,
        );
        setIsLoading(true);
        fetchAndSetProducts(
            newDimensionId,
            1,
            data.Sorting.SelectedSortOption.Key,
            searchTerm,
            currentPageSize,
            rdParameter,
            viParameter,
        ).then(() => {
            // history.push(newPageUrl);
            // window.location.replace(newPageUrl);
            window.history.pushState({}, null, newPageUrl);
            setIsLoading(false);
        });
    };

    const isSearchPage = useMemo(() => {
        return searchTerm !== '';
    }, [searchTerm]);

    const isOnAirPage = useMemo(() => {
        return (
            tscConfig.AliasDimensionOnAirRecently === pageDimensionId ||
            tscConfig.AliasDimensionOnAirToday === pageDimensionId ||
            tscConfig.AliasDimensionOnAirYesterday === pageDimensionId
        );
    }, [pageDimensionId]);

    /*
  console.log('%c jsonLD, isJsonLDTrue:', 'color: red; font-weight: bold;');
  console.log({ jsonLD, isJsonLDTrue: jsonLD.length > 0 });
  */

    const sortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageUrl = buildPageUrl(
            pageDimensionId,
            1,
            e.target.value,
            searchTerm,
            currentPageSize,
            rdParameter,
            viParameter,
        );
        setIsLoading(true);
        fetchAndSetProducts(
            pageDimensionId,
            1,
            e.target.value,
            searchTerm,
            currentPageSize,
            rdParameter,
            viParameter,
        ).then(() => {
            window.history.pushState({}, null, newPageUrl);
            setIsLoading(false);
            setPageSortKey(data.Sorting.SelectedSortOption.Key);
        });
    };

    if (!isFetchComplete) {
        return (
            <section className="tsc-container">
                <p className="prp__fetch-loading">{t('loading')}</p>
            </section>
        );
    }

    if (isFetchComplete && data.Paging.TotalRecords === 0) {
        return (
            <section className="tsc-container">
                <PrpNoSearchResults
                    noResult={{
                        heading: t('noSearchResultHeading'),
                        text: t('noSearchResultText'),
                        buttonText: t('noSearchResultButtonText'),
                    }}
                />
            </section>
        );
    }

    return (
        <>
            {showFraudModal && <FraudModal onCloseClickListener={() => setShowFraudModal(false)} />}
            <section id="to-scroll-position" className="m-auto w-full max-w-[1920px] px-5">
                {isSearchPage && (
                    <h2 className="prp__showing-results">
                        {t('showingResultsFor')} &quot;{decodeURIComponent(searchTerm)}&quot;
                    </h2>
                )}
                {isOnAirPage && <OnAirButtons className="prp-filter-panel__on-air--on-page" />}
                <div className="prp" onKeyDown={prpKeyDownListener}>
                    {isLoading && <div className="prp__loading">Loading</div>}
                    {jsonLD.length > 0 && (
                        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonLD }} />
                    )}
                    {isFilterPanelOpen && (
                        <PrpFilterPanel
                            dimensionStates={data.DimensionStates}
                            queryDims={data.QueryDims}
                            onCloseListener={() => setIsFilterPanelOpen(false)}
                            onFilterChangeListener={onFilterChange}
                            onClearAllClickListener={removeAllAppliedFilters}
                            appliedFilters={appliedFilters}
                            totalProductCount={data.Paging.TotalRecords}
                            isSearchPage={isSearchPage}
                            isOnAirPage={isOnAirPage}
                        />
                    )}
                    <div className="prp__wrapper">
                        <div className="prp__count-and-shorting">
                            <div className="prp__count-and-shorting__product-count">
                                {data.Paging.TotalRecords} {t('products')}
                            </div>
                            <button className="prp__count-and-shorting__filter-button" onClick={filterPanelHandler}>
                                {t('filter')}
                            </button>
                            <select
                                className="prp__count-and-shorting__shorting"
                                defaultValue={data.Sorting.SelectedSortOption.Key}
                                onChange={sortChange}
                            >
                                {data.Sorting.SortOptions.map((sortOption) => (
                                    <option key={sortOption.Key} value={sortOption.Key}>
                                        {sortOption.DisplayName}
                                    </option>
                                ))}
                            </select>
                        </div>
                        {appliedFilters.length > 0 && (
                            <div className="prp__applied-filters">
                                {appliedFilters.map((filter) => (
                                    <button
                                        key={filter.DimensionId}
                                        className="prp__applied-filters__filter"
                                        onClick={() => removeAppliedFilter(filter.DimensionId)}
                                    >
                                        {(filter.DimensionType === 'TSC_d_reviews' ||
                                            filter.DimensionType === 'Reviews') && (
                                            <img
                                                className="prp-filter-panel__filter-list__item-label-raiting--self prp__applied-filters__filter__stars"
                                                src={ratingSvgMapping[filter.DimensionName[0]]}
                                                alt=""
                                            />
                                        )}
                                        <span className="prp__applied-filters__filter__name">
                                            {filter.DimensionName}
                                        </span>
                                        <svg
                                            width={12}
                                            height={12}
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="prp__applied-filters__filter__remove"
                                        >
                                            <g
                                                stroke="#000"
                                                strokeWidth={2}
                                                fill="none"
                                                fillRule="evenodd"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            >
                                                <path d="M1.238 1.238l9.524 9.524M10.762 1.238l-9.524 9.524" />
                                            </g>
                                        </svg>
                                    </button>
                                ))}
                                <button
                                    className="prp__applied-filters__filter prp__applied-filters__filter--clear-all"
                                    onClick={removeAllAppliedFilters}
                                >
                                    {t('clearAll')}
                                </button>
                            </div>
                        )}
                        <div className="prp__product-grid" onKeyDown={(e) => productGridKeyListener(e)}>
                            {cards}
                        </div>
                        {/* <button className="prp__top-button" onClick={scrollToTop}>
            <span className="prp__top-button__text">{t('top')}</span>
            <div className="prp__top-button-icon-wrapper">
              <svg xmlns="http://www.w3.org/2000/svg" className="chevron--top" viewBox="0 0 9 6">
                <g
                  fill="none"
                  fillRule="evenodd"
                  stroke="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="1"
                >
                  <g className="chevron-stroke--white" strokeWidth="2" transform="translate(-43 -16)">
                    <g transform="rotate(180 25.111 10.056)">
                      <path
                        d="M1.55555556 -1.55555556L4.66666667 1.55555556 1.55555556 4.66666667"
                        transform="rotate(90 3.111 1.556)"
                      ></path>
                    </g>
                  </g>
                </g>
              </svg>
            </div>
          </button> */}
                    </div>
                </div>
                <div className="horizontal-divider" style={{ marginTop: '20px' }}></div>
                <div className="prp__pagination-desc">
                    <span className="prp__pagination-desc__display-text">{t('displayingItems')}:</span>
                    <span className="prp__pagination-desc__product-count-text">
                        {(data.Paging.CurrentPage - 1) * (isAdCardAvailable ? currentPageSize - 1 : currentPageSize) +
                            1}{' '}
                        -{' '}
                        {Math.min(
                            data.Paging.CurrentPage * (isAdCardAvailable ? currentPageSize - 1 : currentPageSize),
                            data.Paging.TotalRecords,
                        )}{' '}
                        {t('outOf')} {data.Paging.TotalRecords}
                    </span>
                </div>
                <div className="horizontal-divider" style={{ marginTop: '15px' }}></div>
                <div className="prp__pagination">
                    <button
                        onClick={
                            currentPage === 1
                                ? (e) => e.preventDefault()
                                : (e) => {
                                      setIsLoading(true);
                                      navigateToPage(
                                          pageDimensionId,
                                          currentPage - 1,
                                          data.Sorting.SelectedSortOption.Key,
                                          searchTerm,
                                          isAdCardAvailable ? currentPageSize - 1 : currentPageSize,
                                          rdParameter,
                                          viParameter,
                                      ).then((prpUrl: string) => {
                                          setIsLoading(false);
                                          window.history.pushState({}, null, prpUrl);
                                      });
                                      scrollToTopOfPRP();
                                  }
                        }
                        className="prp__pagination__prev-link"
                        style={{ opacity: currentPage === 1 ? 0 : 1 }}
                    >
                        <span className="visually-hidden">{t('previous')}</span>
                        <svg xmlns="http://www.w3.org/2000/svg" className="chevron" viewBox="0 0 7 10">
                            <g fill="none" fillRule="evenodd" stroke="none" strokeWidth="1">
                                <g transform="translate(-1161 -3756)">
                                    <path className="chevron-fill" d="M0 0H1200V4300H0z"></path>
                                    <g
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        transform="translate(0 3625)"
                                        className="chevron-stroke"
                                    >
                                        <g transform="translate(0 56)">
                                            <g transform="translate(30)">
                                                <g transform="translate(0 73.5)">
                                                    <g transform="translate(1127.5 2.5)">
                                                        <g transform="rotate(-180 7 4.5)">
                                                            <g transform="rotate(-90 7 2)">
                                                                <path
                                                                    d="M2 -2L6 2 2 6"
                                                                    transform="rotate(90 4 2)"
                                                                ></path>
                                                            </g>
                                                        </g>
                                                    </g>
                                                </g>
                                            </g>
                                        </g>
                                    </g>
                                </g>
                            </g>
                        </svg>
                    </button>

                    <div className="prp__pagination__pages">
                        {paginationIndices.map((index) => (
                            <button
                                key={index}
                                className={classNames('prp__pagination__pages__page', {
                                    'prp__pagination__pages__page--current': index === data.Paging.CurrentPage,
                                })}
                                onClick={(e) => {
                                    setIsLoading(true);
                                    navigateToPage(
                                        pageDimensionId,
                                        index,
                                        data.Sorting.SelectedSortOption.Key,
                                        searchTerm,
                                        isAdCardAvailable ? currentPageSize - 1 : currentPageSize,
                                        rdParameter,
                                        viParameter,
                                    ).then((prpUrl: string) => {
                                        setIsLoading(false);
                                        window.history.pushState({}, null, prpUrl);
                                    });
                                    scrollToTopOfPRP();
                                }}
                            >
                                {index}
                            </button>
                        ))}
                    </div>

                    <button
                        onClick={
                            currentPage === data.Paging.TotalPages
                                ? (e) => e.preventDefault()
                                : (e) => {
                                      setIsLoading(true);
                                      navigateToPage(
                                          pageDimensionId,
                                          currentPage + 1,
                                          data.Sorting.SelectedSortOption.Key,
                                          searchTerm,
                                          isAdCardAvailable ? currentPageSize - 1 : currentPageSize,
                                          rdParameter,
                                          viParameter,
                                      ).then((prpUrl: string) => {
                                          setIsLoading(false);
                                          window.history.pushState({}, null, prpUrl);
                                      });
                                      scrollToTopOfPRP();
                                  }
                        }
                        className="prp__pagination__next-link"
                        style={{ opacity: currentPage === data.Paging.TotalPages ? 0 : 1 }}
                    >
                        <span className="visually-hidden">{t('next')}</span>
                        <svg xmlns="http://www.w3.org/2000/svg" className="chevron" viewBox="0 0 7 10">
                            <g fill="none" fillRule="evenodd" stroke="none" strokeWidth="1">
                                <g transform="translate(-1161 -3757)">
                                    <path className="chevron-fill" d="M0 0H1200V4300H0z"></path>
                                    <g
                                        className="chevron-stroke"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        transform="translate(0 3625)"
                                    >
                                        <g transform="translate(0 56)">
                                            <g transform="translate(30)">
                                                <g transform="translate(0 73.5)">
                                                    <g transform="translate(1127.5 2.5)">
                                                        <g transform="rotate(-90 7 2)">
                                                            <path d="M2 -2L6 2 2 6" transform="rotate(90 4 2)"></path>
                                                        </g>
                                                    </g>
                                                </g>
                                            </g>
                                        </g>
                                    </g>
                                </g>
                            </g>
                        </svg>
                    </button>
                </div>
            </section>
        </>
    );
};

export default Prp;
